import { useEffect, useRef } from 'react';

/**
 * Fireworks (Dan, 2026-09-08): a little burst when Kora arrives, a big one when a schedule
 * lands. Plain canvas, no library, no assets — a few hundred particles with gravity and
 * fade, drawn over the page in a fixed layer that never catches a tap. Honours
 * `prefers-reduced-motion` by drawing nothing at all.
 */

export type FireworksSize = 'small' | 'big';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  ttl: number;
  hue: number;
  size: number;
}

const PLANS: Record<
  FireworksSize,
  { bursts: number; gapMs: number; particles: number; ms: number }
> = {
  small: { bursts: 3, gapMs: 260, particles: 42, ms: 2600 },
  big: { bursts: 12, gapMs: 220, particles: 90, ms: 5200 },
};

function reducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function burst(particles: Particle[], x: number, y: number, count: number, hue: number): void {
  for (let i = 0; i < count; i += 1) {
    const angle = (Math.PI * 2 * i) / count + Math.random() * 0.2;
    const speed = 2.2 + Math.random() * 3.4;
    particles.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 0,
      ttl: 900 + Math.random() * 700,
      hue: hue + Math.random() * 40 - 20,
      size: 1.6 + Math.random() * 1.8,
    });
  }
}

export function Fireworks({
  size,
  onDone,
}: {
  size: FireworksSize;
  onDone?: () => void;
}): JSX.Element | null {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const done = useRef(onDone);
  done.current = onDone;

  useEffect(() => {
    if (reducedMotion()) {
      done.current?.();
      return undefined;
    }
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;

    const plan = PLANS[size];
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = (): void => {
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    const particles: Particle[] = [];
    const timers: number[] = [];
    for (let i = 0; i < plan.bursts; i += 1) {
      timers.push(
        window.setTimeout(() => {
          const w = canvas.width / dpr;
          const h = canvas.height / dpr;
          burst(
            particles,
            w * (0.15 + Math.random() * 0.7),
            h * (size === 'big' ? 0.12 + Math.random() * 0.45 : 0.18 + Math.random() * 0.3),
            plan.particles,
            Math.random() * 360,
          );
        }, i * plan.gapMs),
      );
    }

    let frame = 0;
    let last = performance.now();
    const started = last;
    const tick = (now: number): void => {
      const dt = Math.min(now - last, 40);
      last = now;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = particles.length - 1; i >= 0; i -= 1) {
        const p = particles[i]!;
        p.life += dt;
        if (p.life >= p.ttl) {
          particles.splice(i, 1);
          continue;
        }
        p.vy += 0.045 * (dt / 16);
        p.vx *= 0.985;
        p.vy *= 0.985;
        p.x += p.vx * (dt / 16);
        p.y += p.vy * (dt / 16);
        const alpha = 1 - p.life / p.ttl;
        ctx.beginPath();
        ctx.fillStyle = `hsla(${p.hue}, 90%, 60%, ${alpha})`;
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      if (now - started < plan.ms || particles.length > 0) {
        frame = requestAnimationFrame(tick);
      } else {
        done.current?.();
      }
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      for (const t of timers) clearTimeout(t);
      window.removeEventListener('resize', resize);
    };
  }, [size]);

  if (reducedMotion()) return null;
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-testid={`fireworks-${size}`}
      className="pointer-events-none fixed inset-0 z-50 h-full w-full"
    />
  );
}

/** Once per browser session per member — so a page revisit doesn't fire it again. */
export function claimOncePerSession(key: string): boolean {
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}
